import React, { useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import "./styles/SowHeader.css";
import {
  V2ProjectResource,
  V2UpdateProjectApiArg,
  useV2CancelApprovalRequestForProjectByIdMutation,
  useV2CreateProjectCollaboratorMutation,
  useV2DeleteProjectMutation,
  useV2DuplicateProjectByIdMutation,
  useV2GetAccountQuery,
  useV2GetProjectByIdQuery,
  useV2ListCollaboratorsForAccountQuery,
  useV2RequestApprovalForProjectByIdMutation,
  useV2RestartScopingForProjectByIdMutation,
  useV2SyncProjectRevenueToCrmMutation,
  useV2UpdateProjectMutation,
  useV2UpdateProjectToLostByIdMutation,
  useV2UpdateProjectToWonByIdMutation,
} from "@generated";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear } from "@fortawesome/pro-light-svg-icons";
import useOauth from "../../utils/customHooks/useOauth";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import { handleAlert } from "../../utils/helperFunctions";
import { withErrorReason } from "@components/CatalogIndex/saveErrorToast";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import CurrencyFormat from "react-currency-format";
import ContextMenu from "@components/ContextMenu/ContextMenu";
import {
  getHeaderStatus,
  getMargin,
  getProfit,
  getSecondaryButtonByStatus,
  statusLabelsFrom,
} from "./utils/sowHeaderHelpers";
import ConfirmationModal from "@components/Modals/ConfirmationModal";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import CreateBlueprintModal from "@components/CreateBlueprintFromProjectModal/CreateBlueprintFromProjectModal";
import DuplicateProjectModal, {
  DUPLICATE_PROJECT_QUEUED_MESSAGE,
} from "@components/DuplicateProjectModal/DuplicateProjectModal";
import { Select2V2 } from "@components/FormsV2";
import { OptionType } from "../../pages/Products/types";
import GenerateDocument from "@components/GenerateDocument/GenerateDocument";
import useFlags from "@common/hooks/useFlags";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import useMountWhileOpen from "@common/hooks/useMountWhileOpen";
import { useActionCable } from "@common/hooks/useActionCable";
import { useApiCable } from "@common/hooks/useApiCable";
import { projectChromeQueryArgs } from "./projectChromeQuery";

type FieldLabels = Record<string, string | undefined>;

// The pricing broadcast carries its own serialization of the project; only
// these display attributes are taken from it, so a push can't clobber the
// v2 status/active/relationships the header's logic reads.
const LIVE_ATTRIBUTES = [
  "project-name",
  "client-name",
  "contract-revenue",
  "contract-cost",
] as const;

type LiveAttributes = {
  "project-name"?: string;
  "client-name"?: string;
  "contract-revenue"?: number;
  "contract-cost"?: number;
};

function SowHeader({ project_id, account_slug, save_to_cloud_storage_flag }) {
  const [authorizationCode] = useOauth();
  const { releaseProjectBlueprintsV2 } = useFlags();
  const appHost = process.env.REACT_APP_DOORKEEPER_APP_URL;
  const projectId = String(project_id);

  // Shared with ProjectNavigationProvider: one cache entry under react-nav.
  const {
    data: projectData,
    isLoading: projectLoading,
    isError: projectLoadFailed,
  } = useV2GetProjectByIdQuery(projectChromeQueryArgs(projectId));
  const { data: accountData } = useV2GetAccountQuery({});
  const { accessLevel } = usePrivilegeCheck();

  const [requestApprovalForProject] =
    useV2RequestApprovalForProjectByIdMutation();
  const [restartScopingForProject] =
    useV2RestartScopingForProjectByIdMutation();
  const [cancelApprovalForProject] =
    useV2CancelApprovalRequestForProjectByIdMutation();
  const [markProjectWon] = useV2UpdateProjectToWonByIdMutation();
  const [markProjectLost] = useV2UpdateProjectToLostByIdMutation();
  const [duplicateProjectById] = useV2DuplicateProjectByIdMutation();
  const [deleteProject] = useV2DeleteProjectMutation();
  const [updateProject] = useV2UpdateProjectMutation();
  const [createProjectCollaborator] = useV2CreateProjectCollaboratorMutation();
  const [syncProjectRevenueToCrm] = useV2SyncProjectRevenueToCrmMutation();

  const [liveAttributes, setLiveAttributes] = useState<LiveAttributes>({});
  const [showFailAlert, setShowFailAlert] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [contextX, setContextX] = useState(0);
  const [contextY, setContextY] = useState(0);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [showConfirmArchiveModal, setShowConfirmArchiveModal] = useState(false);
  const [showConfirmDuplicateModal, setShowConfirmDuplicateModal] =
    useState(false);
  const [showDuplicateProjectModal, setShowDuplicateProjectModal] =
    useState(false);
  const [showCreateBlueprintModal, setShowCreateBlueprintModal] =
    useState(false);
  const [showAddTeammatesModal, setShowAddTeammatesModal] = useState(false);
  const [teammates, setTeammates] = useState<OptionType[] | []>([]);
  const [showGenerateDocumentModal, setShowGenerateDocumentModal] =
    useState(false);

  // The three modals mount only while open (their data hooks fire on mount),
  // and stay mounted through the close transition so they fade out.
  const createBlueprintMounted = useMountWhileOpen(showCreateBlueprintModal);
  const duplicateProjectMounted = useMountWhileOpen(showDuplicateProjectModal);
  const generateDocumentMounted = useMountWhileOpen(showGenerateDocumentModal);

  // Fetched the first time Invite Teammates opens, not on page load.
  const { data: collaboratorsData } = useV2ListCollaboratorsForAccountQuery(
    { page: { size: 200 } },
    { skip: !showAddTeammatesModal }
  );
  const teammateOptions = useMemo(
    () =>
      (collaboratorsData?.data ?? []).map((teammate) => ({
        value: teammate.id,
        label: teammate.attributes?.name,
      })),
    [collaboratorsData]
  );

  const fetchedProject = projectData?.data;
  const project: V2ProjectResource | undefined = useMemo(
    () =>
      fetchedProject && {
        ...fetchedProject,
        attributes: { ...fetchedProject.attributes, ...liveAttributes },
      },
    [fetchedProject, liveAttributes]
  );

  // `active` and `client-name` are serialized by V2::ProjectResource but
  // missing from its swagger schema, so they aren't on the generated type.
  const projectAttributes = (project?.attributes ?? {}) as NonNullable<
    V2ProjectResource["attributes"]
  > & { active?: boolean; "client-name"?: string };
  const projectStatus = projectAttributes.status ?? "building";
  const projectIsActive = projectAttributes.active !== false;
  const projectFieldLabels = projectAttributes["field-labels"] as
    | FieldLabels
    | undefined;
  const rescopeLabel = projectFieldLabels?.rescope || "Re-Scope";
  const currencyUnit = projectFieldLabels?.currency_unit ?? "";

  const accountAttributes = accountData?.data?.attributes;
  const accountFieldLabels = accountAttributes?.["field-labels"] as
    | FieldLabels
    | undefined;
  const isPsa = accountAttributes?.["psa?"];

  const statusToDisplay = getHeaderStatus(
    projectStatus,
    statusLabelsFrom(accountFieldLabels)
  );

  let buttonStatus = projectStatus.includes("approval")
    ? "approval"
    : projectStatus;
  if ((buttonStatus === "approved" || buttonStatus === "won") && isPsa) {
    buttonStatus = buttonStatus + "Psa";
  }
  const secondaryButton = getSecondaryButtonByStatus(
    buttonStatus,
    projectFieldLabels
  );

  // The v1 header also compared the external request's status here, but its
  // `a !== x || a !== y` test was always true, so every previously approved
  // project offered Re-Scope. Kept as-is.
  const needsRescope =
    projectStatus === "won" ||
    projectStatus === "lost" ||
    projectStatus === "approved";

  const duplicatePrivilege = accessLevel("projects.duplicate") ?? "none";
  const blueprintPrivilege = accessLevel("settings.blueprints") ?? "none";
  const mergeDataPrivilege = accessLevel("projects.merge_data") ?? "none";
  // Default 'on' for backwards compatibility
  const requestApprovalPrivilege =
    accessLevel("projects.request_approval") ?? "manage";
  const canManageApproval = requestApprovalPrivilege === "manage";
  const canManageOverview =
    (accessLevel("projects.overview") ?? "manage") === "manage";
  // Request Approval (building) and Cancel Approval (approval) both need
  // request_approval at manage. Matched on status, not the button text, since
  // accounts can relabel Request Approval.
  const secondaryButtonAllowed =
    (buttonStatus !== "building" && buttonStatus !== "approval") ||
    canManageApproval;

  type ActionCablePayload = {
    data: { id: string | number; attributes?: Record<string, unknown> };
  };

  const apiCable = useApiCable(account_slug);

  useActionCable<ActionCablePayload>({
    ...apiCable,
    channel: "ProjectPricingChannel",
    params: { account_slug },
    onReceived: (data) => {
      if (String(data.data.id) !== projectId) return;

      const pushed = data.data.attributes ?? {};
      setLiveAttributes(
        Object.fromEntries(
          LIVE_ATTRIBUTES.filter((key) => key in pushed).map((key) => [
            key,
            pushed[key],
          ])
        ) as LiveAttributes
      );
    },
  });

  const showFailure = (message: string, err: unknown) => {
    setErrorMessage(withErrorReason(message, err));
    setShowFailAlert(true);
  };

  // Status changes reload the page so the legacy chrome around the header
  // picks up the new status too.
  const runAndReload = (
    action: Promise<unknown>,
    success: string,
    failure: string
  ) =>
    action
      .then(() => {
        setSuccessMessage(success);
        setShowSuccessAlert(true);
        window.location.reload();
      })
      .catch((err) => showFailure(failure, err));

  const requestApproval = () =>
    runAndReload(
      requestApprovalForProject({ id: projectId }).unwrap(),
      "The Project has been sent for approval!",
      "Could not request approval."
    );

  const reScope = () =>
    runAndReload(
      restartScopingForProject({ id: projectId }).unwrap(),
      "The Project has been resubmitted for scoping!",
      "Could not re-scope the project."
    );

  const cancelApproval = () =>
    runAndReload(
      cancelApprovalForProject({ id: projectId }).unwrap(),
      "The Project approval has been canceled!",
      "Could not cancel the approval."
    );

  const pushToPsa = () => {
    let url = `/projects/${project_id}/psa_project`;
    if (project?.relationships?.["psa-project"]?.data) {
      url += "/edit";
    } else {
      url += "/new";
    }
    window.open(url, "_blank");
  };

  const archive = () => {
    setShowConfirmArchiveModal(false);
    setContextMenuOpen(false);

    // DELETE soft-deletes (archives); PATCH active: true restores. `active`
    // isn't on the generated create/update body type.
    const unarchiveBody = {
      data: { type: "projects", id: projectId, attributes: { active: true } },
    } as unknown as V2UpdateProjectApiArg["body"];

    const promise = projectIsActive
      ? deleteProject({ id: projectId }).unwrap()
      : updateProject({ id: projectId, body: unarchiveBody }).unwrap();

    promise
      .then(() => {
        setSuccessMessage(
          `This project has been successfully ${
            projectIsActive ? "archived" : "un-archived"
          }!`
        );
        setShowSuccessAlert(true);
        if (projectIsActive) {
          window.location.replace(`/projects`);
        } else {
          window.location.reload();
        }
      })
      .catch((err) =>
        showFailure(
          `Could not ${
            projectIsActive ? "archive" : "un-archive"
          } the project.`,
          err
        )
      );
  };

  const markWon = () =>
    runAndReload(
      markProjectWon({ id: projectId }).unwrap(),
      "The Project has been marked as won!",
      "Could not mark the project as won."
    );

  const markLost = () =>
    runAndReload(
      markProjectLost({ id: projectId }).unwrap(),
      "The Project has been marked as lost!",
      "Could not mark the project as lost."
    );

  const duplicateProject = () => {
    setShowConfirmDuplicateModal(false);
    setContextMenuOpen(false);
    duplicateProjectById({ id: projectId })
      .unwrap()
      .then(() => {
        setSuccessMessage(DUPLICATE_PROJECT_QUEUED_MESSAGE);
        setShowSuccessAlert(true);
      })
      .catch((err) => showFailure("Could not duplicate the project.", err));
  };

  const addTeammates = () => {
    setShowAddTeammatesModal(false);
    setContextMenuOpen(false);
    if (teammates.length === 0) return;

    runAndReload(
      Promise.all(
        teammates.map((teammate) =>
          createProjectCollaborator({
            body: {
              data: {
                type: "project-collaborators",
                relationships: {
                  project: { data: { type: "projects", id: projectId } },
                  user: { data: { type: "users", id: String(teammate.value) } },
                },
              },
            },
          }).unwrap()
        )
      ),
      "Teammates added!",
      "Could not add teammates."
    );
  };

  const syncCrm = () =>
    runAndReload(
      syncProjectRevenueToCrm({ id: projectId }).unwrap(),
      "CRM Synced!",
      "Could not update the CRM."
    );

  const closeContextMenu = () => {
    setContextMenuOpen(false);
    document.removeEventListener("click", closeContextMenu);
  };

  const getContextMenuItems = () => {
    let menuItems = [
      {
        newTab: false,
        onClick: () => {
          releaseProjectBlueprintsV2
            ? setShowDuplicateProjectModal(true)
            : setShowConfirmDuplicateModal(true);
        },
        iconClass: "",
        style: { padding: "0 0.5em 0 0" },
        name: "Duplicate",
        hasBreakingLine: false,
      },
      {
        newTab: false,
        onClick: () =>
          releaseProjectBlueprintsV2
            ? setShowCreateBlueprintModal(true)
            : window.open(
                `/projects/${project_id}/sow_blueprints/new`,
                "_blank"
              ),
        iconClass: "",
        style: { padding: "0 0.5em 0 0" },
        name: "Create Blueprint",
        hasBreakingLine: false,
      },
      {
        newTab: false,
        onClick: () => {
          setShowConfirmArchiveModal(true);
        },
        iconClass: "",
        style: { padding: "0 0.5em 0 0" },
        name: "Archive",
        hasBreakingLine: false,
      },
      {
        newTab: false,
        onClick: () => {
          reScope();
        },
        iconClass: "",
        style: { padding: "0 0.5em 0 0" },
        name: rescopeLabel,
        hasBreakingLine: true,
      },
      {
        newTab: false,
        onClick: () => {
          markWon();
        },
        iconClass: "",
        style: { padding: "0 0.5em 0 0" },
        name: "Deal Won",
        hasBreakingLine: false,
      },
      {
        newTab: false,
        onClick: () => {
          markLost();
        },
        iconClass: "",
        style: { padding: "0 0.5em 0 0" },
        name: "Deal Lost",
        hasBreakingLine: true,
      },
      {
        newTab: false,
        onClick: () => {
          setShowAddTeammatesModal(true);
        },
        iconClass: "",
        style: { padding: "0 0.5em 0 0" },
        name: "Invite Teammates",
        hasBreakingLine: true,
      },
      {
        newTab: false,
        onClick: () => {
          syncCrm();
        },
        iconClass: "",
        style: { padding: "0 0.5em 0 0" },
        name: "Update CRM",
        hasBreakingLine: true,
      },
      {
        newTab: false,
        onClick: () => {
          window.open(
            `${appHost}/projects/${project_id}/merge_data_visualization?version=1`,
            "_blank"
          );
        },
        iconClass: "",
        style: { padding: "0 0.5em 0 0" },
        name: "View Merge Fields",
        hasBreakingLine: false,
      },
      {
        newTab: false,
        onClick: () => {
          window.open(
            `${appHost}/projects/${project_id}/merge_data_visualization?version=2`,
            "_blank"
          );
        },
        iconClass: "",
        style: { padding: "0 0.5em 0 0" },
        name: "View Merge Fields V2",
        hasBreakingLine: false,
      },
    ];
    if (!needsRescope || !canManageApproval) {
      menuItems = menuItems.filter((item) => item.name !== rescopeLabel);
    }
    if (
      (projectStatus !== "approved" && projectStatus !== "won") ||
      (blueprintPrivilege !== "create" && blueprintPrivilege !== "manage")
    ) {
      menuItems = menuItems.filter((item) => item.name !== "Create Blueprint");
    }
    if (projectStatus !== "building") {
      menuItems = menuItems.filter((item) => item.name !== "Invite Teammates");
    }
    if (projectStatus === "lost") {
      menuItems = menuItems.filter((item) => item.name !== "Update CRM");
    }
    if (!projectIsActive) {
      menuItems = [
        {
          newTab: false,
          onClick: () => {
            setShowConfirmArchiveModal(true);
          },
          iconClass: "",
          style: { padding: "0 0.5em 0 0" },
          name: "Un-archive",
          hasBreakingLine: false,
        },
      ];
    }
    if (!canManageOverview) {
      menuItems = menuItems.filter(
        (item) =>
          item.name !== "Archive" &&
          item.name !== "Un-archive" &&
          item.name !== "Deal Won" &&
          item.name !== "Deal Lost" &&
          item.name !== "Update CRM"
      );
    }
    if (duplicatePrivilege === "none") {
      menuItems = menuItems.filter((item) => item.name !== "Duplicate");
    }
    if (mergeDataPrivilege === "none") {
      menuItems = menuItems.filter(
        (item) =>
          item.name !== "View Merge Fields" &&
          item.name !== "View Merge Fields V2"
      );
    }
    return menuItems;
  };

  if (projectLoading) {
    return <SmallSpinner />;
  }

  // Without a project the status/active defaults above would render a live
  // "Building" header with working actions, so render nothing actionable.
  // A failed background refetch keeps the last good data and renders it.
  if (projectLoadFailed && !fetchedProject) {
    return (
      <div className="sowHeader">
        <div className="headerContainer">
          <div className="projectName" style={{ marginLeft: "16px" }}>
            This project could not be loaded.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sowHeader">
      {handleAlert(
        showFailAlert,
        errorMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      <div className="headerContainer">
        {contextMenuOpen && (
          <ContextMenu
            portal
            menuItems={getContextMenuItems()}
            style={{
              zIndex: "100",
              left: contextX,
              top: contextY,
              boxShadow: "0px 2px 3px 0px rgba(0, 0, 0, 0.15)",
            }}
          />
        )}
        <div style={{ marginLeft: "16px" }}>
          <div className="projectName">
            <span>
              {projectAttributes["project-name"]}&nbsp;
              <FontAwesomeIcon
                icon={faGear}
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  let width = e.pageX;
                  setContextX(width);
                  let height = e.pageY;
                  setContextY(height);
                  setContextMenuOpen(!contextMenuOpen);
                  document.addEventListener("click", closeContextMenu);
                }}
              />
            </span>
          </div>
          <div className="flexCenter">
            <div className={statusToDisplay.class}>{statusToDisplay.text}</div>
            <div className="projectData">
              <span>
                <strong>Client: </strong>
                {projectAttributes["client-name"]}
              </span>
              &nbsp;&nbsp;&nbsp; |&nbsp;&nbsp;&nbsp;
              <span>
                <strong>Revenue: </strong>{" "}
                <CurrencyFormat
                  displayType="text"
                  prefix={currencyUnit}
                  isNumericString
                  thousandSeparator
                  value={Number(
                    projectAttributes["contract-revenue"] || 0
                  ).toFixed(2)}
                />
              </span>
              &nbsp;&nbsp;&nbsp;
              <span className="cost">
                <strong>Cost: </strong>{" "}
                <CurrencyFormat
                  displayType="text"
                  prefix={currencyUnit}
                  isNumericString
                  thousandSeparator
                  value={Number(
                    projectAttributes["contract-cost"] || 0
                  ).toFixed(2)}
                />
              </span>
              &nbsp;&nbsp;&nbsp;
              <span className="profit">
                <strong>Profit: </strong>{" "}
                <CurrencyFormat
                  displayType="text"
                  prefix={currencyUnit}
                  isNumericString
                  thousandSeparator
                  value={getProfit(project)}
                />
              </span>
              &nbsp;&nbsp;&nbsp;
              <span className="profit">
                <strong>Margin: </strong> {getMargin(project)}%
              </span>
            </div>
          </div>
        </div>
        <div className="actionButtons projectActionButtons">
          {projectIsActive &&
            secondaryButton.text !== "" &&
            secondaryButtonAllowed && (
              <Button
                className={`${secondaryButton.class} marginRight10`}
                onClick={() => {
                  switch (secondaryButton.text) {
                    case "Request Approval":
                    case projectFieldLabels?.request_approval:
                      requestApproval();
                      break;
                    case rescopeLabel:
                      reScope();
                      break;
                    case "Cancel Approval":
                      cancelApproval();
                      break;
                    case "Push To PSA":
                      pushToPsa();
                      break;
                    default:
                      break;
                  }
                }}
              >
                {secondaryButton.text}
              </Button>
            )}
          {projectIsActive && (
            <Button
              className="squareGreenButton"
              onClick={() => setShowGenerateDocumentModal(true)}
            >
              Generate Document
            </Button>
          )}
        </div>
      </div>
      <ConfirmationModal
        show={showConfirmArchiveModal}
        title={projectIsActive ? "Archive Project" : "Un-archive Project"}
        message={`Are you sure you want to ${
          projectIsActive ? "archive" : "un-archive"
        } this project?`}
        onConfirm={() => archive()}
        onCancel={() => setShowConfirmArchiveModal(false)}
      />
      <ConfirmationModal
        show={showConfirmDuplicateModal}
        title="Duplicate Project"
        message={`Are you sure you want to duplicate this project?`}
        onConfirm={() => duplicateProject()}
        onCancel={() => setShowConfirmDuplicateModal(false)}
      />
      {createBlueprintMounted && (
        <CreateBlueprintModal
          projectId={projectId}
          show={showCreateBlueprintModal}
          onClose={() => setShowCreateBlueprintModal(false)}
        />
      )}
      {duplicateProjectMounted && (
        <DuplicateProjectModal
          projectId={projectId}
          show={showDuplicateProjectModal}
          onClose={() => setShowDuplicateProjectModal(false)}
          onSuccess={(message) => {
            setSuccessMessage(message);
            setShowSuccessAlert(true);
          }}
        />
      )}
      <ScopeStackModal
        modalTitle="Add Teammates"
        modalBody={
          <div>
            <Select2V2
              label={"Invite Teammates"}
              options={teammateOptions}
              isDisabled={false}
              defaultValue={undefined}
              onChange={(e) => {
                setTeammates(e);
              }}
              isMulti={true}
            />
          </div>
        }
        button1Text="Cancel"
        handleButton1Click={() => setShowAddTeammatesModal(false)}
        button2Text={"Add"}
        handleButton2Click={() => {
          addTeammates();
        }}
        button2Disabled={teammates?.length === 0}
        show={showAddTeammatesModal}
        handleClose={() => setShowAddTeammatesModal(false)}
      />
      {generateDocumentMounted && (
        <GenerateDocument
          orientation="horizontal"
          show={showGenerateDocumentModal}
          setShow={setShowGenerateDocumentModal}
          account_slug={account_slug}
          project_id={project_id}
          saveToCloudStorageFlag={save_to_cloud_storage_flag}
          isStationary={false}
          authorizationCode={authorizationCode}
          projectPassed={project}
          onDownloadError={(message) => {
            setErrorMessage(message);
            setShowFailAlert(true);
          }}
        />
      )}
    </div>
  );
}

export default SowHeader;
